import { yupResolver } from "@hookform/resolvers/yup";
import { CircularProgress, MenuItem, SelectChangeEvent } from "@mui/material";
import axios, { AxiosResponse } from "axios";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Select, { StylesConfig, Theme } from "react-select";
import ReactToPrint, { useReactToPrint } from "react-to-print";
import * as yup from "yup";

import Button from "components/Button";
import ListCardSkeleton from "components/ListCardSkeleton";
import PriceTableSelect from "components/PriceTableSelect";
import SelectDropdown from "components/SelectDropdown";
import SelectInput from "components/SelectInput";
import SelectSearch from "components/SelectSearch";
import { API_URL } from "config";
import * as FS from "pages/Quotes/ProfessionalQuote/components/Forms/formStyles";
import { useAuth } from "providers/Auth";
import { useCompany } from "providers/Company";
import { useProfessionalQuote } from "providers/Quote/ProfessionalQuote";
import { useUser } from "providers/User";
import { api } from "services";
import {
  PaymentFeeOwner,
  PaymentMethod,
  PaymentTool,
  RoleDescription,
  TypePriceTable,
} from "types/enums";
import { BrazilianCity, BrazilianState } from "types/ibge";
import {
  ParcelValue,
  QuoteSimulationResponse,
  SimulateData,
  SimulatorParcel,
} from "types/quotes";
import { getBrazilianStates } from "utils/brazilianStates";
import debounce from "utils/debounce";
import {
  getCompanyOptions,
  getCreditPriceTableOptions,
} from "utils/getOptions";
import { handleTwoDecimalPlacesInput } from "utils/handleInput";
import {
  financingAmountOptions,
  financingOptionsInterface,
} from "utils/selectOptions";
import { toast } from "utils/toast";

import PDFSimulatorParcels from "./components/PdfSimulatorParcels";
import SimulatorParcelCard from "./components/SimulatorParcelCard";
import * as S from "./styles";

const customTheme = (theme: Theme) => ({
  ...theme,
  borderRadius: 7,
  colors: {
    ...theme.colors,
    /*
     * multiValue(remove)/color:hover
     */
    danger: "var(--danger)",
    /*
     * multiValue(remove)/backgroundColor(focused)
     * multiValue(remove)/backgroundColor:hover
     */
    dangerLight: "var(--danger-light)",
    /*
     * control/backgroundColor
     * menu/backgroundColor
     * option/color(selected)
     */
    neutral0: "var(--neutral-0)",
    /*
     * control/backgroundColor(disabled)
     */
    neutral5: "var(--neutral-5)",
    /*
     * control/borderColor(disabled)
     * multiValue/backgroundColor
     * indicators(separator)/backgroundColor(disabled)
     */
    neutral10: "var(--neutral-10)",
    /*
     * control/borderColor
     * option/color(disabled)
     * indicators/color
     * indicators(separator)/backgroundColor
     * indicators(loading)/color
     */
    neutral20: "var(--neutral-20)",
    /*
     * control/borderColor(focused)
     * control/borderColor:hover
     */
    neutral30: "var(--neutral-30)",
    /*
     * menu(notice)/color
     * singleValue/color(disabled)
     * indicators/color:hover
     */
    neutral40: "var(--neutral-40)",
    /*
     * placeholder/color
     */
    neutral50: "var(--neutral-50)",
    /*
     * indicators/color(focused)
     * indicators(loading)/color(focused)
     */
    neutral60: "var(--neutral-60)",
    neutral70: "var(--neutral-70)",
    /*
     * input/color
     * multiValue(label)/color
     * singleValue/color
     * indicators/color(focused)
     * indicators/color:hover(focused)
     */
    neutral80: "var(--neutral-80)",
    neutral90: "var(--neutral-90)",
    /*
     * control/boxShadow(focused)
     * control/borderColor(focused)
     * control/borderColor:hover(focused)
     * option/backgroundColor(selected)
     * option/backgroundColor:active(selected)
     */
    primary: "var(--primary)",
    /*
     * option/backgroundColor(focused)
     */
    primary25: "var(--primary-25)",
    /*
     * option/backgroundColor:active
     */
    primary50: "var(--primary-50)",
    primary75: "var(--primary-75)",
  },
});

const customStyles: StylesConfig = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    height: 48,
    borderColor: "var(--border)",
    cursor: "pointer",
    "&:hover": {},
  }),
  singleValue: (base: Record<string, unknown>) => ({
    ...base,
    textTransform: "uppercase",
  }),
  option: (base: Record<string, unknown>) => ({
    ...base,
    cursor: "pointer",
    textTransform: "uppercase",
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 100,
  }),
};

interface SimulatorProps {
  onHide: () => void;
}

const Simulator = ({ onHide }: SimulatorProps) => {
  const { userRole } = useAuth();
  const {
    professionalCompanies,
    isLoadingProfessionalCompanies,
    getProfessionalCompanies,
  } = useCompany();
  const { simulatedQuotePayment, setSimulatedQuotePayment } =
    useProfessionalQuote();
  const { userData } = useUser();

  const [isLoading, setIsLoading] = useState(false);
  const [simulatorParcels, setSimulatorParcels] = useState<SimulatorParcel[]>(
    []
  );
  const [brazilianStates, setBrazilianStates] = useState<BrazilianState[]>([]);
  const [brazilianCities, setBrazilianCities] = useState<BrazilianCity[]>([]);
  const [financingValueOptions, setFinancingValueOptions] = useState<
    financingOptionsInterface[]
  >(financingAmountOptions);
  const componentRef = useRef(null);

  const schema = yup.object().shape({
    value: yup
      .mixed()
      .when("method", {
        is: PaymentMethod.Credit,
        then: yup.string(),
        otherwise: yup.string().transform((value) => value.value),
      })
      .required("*Campo obrigatório"),
    firstDate: yup.string().default(moment().toISOString(true)),
    company: yup
      .object()
      .required("*Campo obrigatório")
      .shape({
        value: yup.number().required("*Campo obrigatório"),
        label: yup.string().required("*Campo obrigatório").uppercase(),
      }),
    method: yup.number(),
    paymentFeeOwner: yup.number().nullable(),
    typePriceTable: yup.number().nullable(),
    priceTable: yup
      .object()
      .required("*Campo obrigatório")
      .shape({
        value: yup.number().required("*Campo obrigatório"),
        label: yup.string().required("*Campo obrigatório").uppercase(),
      }),
    paymentTool: yup.number().nullable(),
    city: yup
      .string()
      .test("selectCity", "*Selecione uma Cidade", (inputValue) =>
        inputValue === "" ? false : true
      )
      .transform((value) => value.toUpperCase()),
    state: yup
      .string()
      .test("selectState", "*Selecione um Estado", (inputValue) =>
        inputValue === "" ? false : true
      ),
  });

  const {
    register,
    handleSubmit,
    setValue,
    resetField,
    watch,
    getValues,
    control,
    formState: { errors },
  } = useForm<SimulateData>({
    resolver: yupResolver(schema),
    defaultValues: {
      method: PaymentMethod.Credit,
      paymentFeeOwner: 1,
      typePriceTable: TypePriceTable.Companies,
      paymentTool: PaymentTool["Link de pagamento"],
    },
  });

  const changePaymentMethod = (paymentMethod: PaymentMethod) => {
    resetField("value");
    resetField("city");
    resetField("state");
    // resetField("company");
    resetField("paymentFeeOwner");
    resetField("priceTable");
    resetField("paymentTool");

    setValue("method", paymentMethod);

    if (paymentMethod === PaymentMethod.Financing) {
      setValue("paymentFeeOwner", null);
      setValue("typePriceTable", null);
    }

    if (paymentMethod === PaymentMethod.Credit) {
      setValue("paymentFeeOwner", PaymentFeeOwner.Paciente);
      setValue("typePriceTable", 1);
    }
  };

  const changeTypePriceTable = (typePriceTable: TypePriceTable) => {
    resetField("value");
    resetField("paymentTool");
    resetField("priceTable");

    setValue("typePriceTable", typePriceTable);

    if (typePriceTable === TypePriceTable.Companies) {
      resetField("priceTable");
    }

    // if (typePriceTable === TypePriceTable.Tables) {
    //   resetField("company");
    // }
  };

  const changePaymentTool = () => {
    resetField("priceTable");
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
      @page { size: A4 portrait; margin: 14mm; }
      @media print {
        html, body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          background: #ffffff;
        }
        table { page-break-inside: auto; }
        tr { page-break-inside: avoid; }
        thead { display: table-header-group; }
      }
    `,
    documentTitle: `simulacao_da_${
      getValues("priceTable")
        ? getValues("priceTable.label")
        : getValues("company.label")
    }_valor_total_${
      typeof getValues("value") == "object"
        ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          getValues("value.value")
        : getValues("value")
    }`,
  });

  useEffect(() => {
    if (userRole !== RoleDescription.Admin) {
      getProfessionalCompanies(userData.id as number);
    }
  }, []);

  const onSubmit = async (data: SimulateData) => {
    setSimulatedQuotePayment({
      ...simulatedQuotePayment,
      paymentFeeOwner: data.paymentFeeOwner,
    });

    setIsLoading(true);

    const body = {
      value: Number(data.value.replace(/\./g, "").replace(",", ".")),
      city:
        data.method === PaymentMethod.Financing
          ? data.city.toUpperCase()
          : undefined,
      state:
        data.method === PaymentMethod.Financing
          ? data.state.toUpperCase()
          : undefined,
      firstDate: data.firstDate,
      method: data.method,
      priceTable: data.priceTable ? data.priceTable.value : undefined,
      paymentFeeOwner:
        data.method !== PaymentMethod.Financing
          ? data.paymentFeeOwner
          : undefined,
      CompanyId: data.company ? data.company.value : undefined,
    };

    try {
      const { data }: AxiosResponse<QuoteSimulationResponse> = await api.post(
        "/Quote/simulate",
        body
      );

      setIsLoading(false);

      setSimulatorParcels(data.parcels);
    } catch (error: any) {
      setIsLoading(false);

      let message = "";
      if (error.response.status === 400) {
        message = (Object.values(error.response.data)[0] as string[])[0];
      } else {
        message = "Ocorreu um erro, tente novamente.";
      }

      toast.fire({
        icon: "error",
        title: message,
      });
    }
  };

  const handleSelectBrazilianState = async (event: SelectChangeEvent<any>) => {
    const brazilianState = event.target.value;

    setValue("city", "");

    const response = await axios.get(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${brazilianState}/municipios?orderBy=nome`
    );

    setBrazilianCities(response.data);
  };

  const handleFinancingValueOptions = async ({
    state,
    city,
    method,
  }: SimulateData) => {
    setIsLoading(true);
    try {
      const { data }: AxiosResponse<ParcelValue[]> = await api.post(
        "Quote/parcelsValues",
        {
          state: state,
          city: city,
          paymentMethod: method,
        }
      );

      setFinancingValueOptions(data);

      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getBrazilianStates(setBrazilianStates);

    return () => {
      setSimulatedQuotePayment({} as SimulatorParcel);
    };
  }, []);

  const debounceSearch = debounce((values) => {
    const numValue = parseInt(values.value.replace(/\./g, ""));

    if (numValue >= 100) {
      onSubmit(values);
    }
  }, 500);

  const validateValues = (values: SimulateData) => {
    if (userRole !== RoleDescription.Admin && !values?.company?.value) {
      return;
    }

    if (userRole === RoleDescription.Admin) {
      if (
        values.typePriceTable === TypePriceTable.Companies &&
        !values?.company?.value
      ) {
        return;
      }
      if (
        values.typePriceTable === TypePriceTable.Tables &&
        !values?.priceTable?.value
      ) {
        return;
      }
    }

    debounceSearch(values);
  };

  const onChangeSearchInput = (e: any) => {
    handleTwoDecimalPlacesInput(e, setValue, "value");

    const values = getValues();

    validateValues(values);
  };

  const [companyCardMachines, setCompanyCardMachines] = useState<
    {
      id: number;
      description: string;
    }[]
  >([]);
  const [isLoadingCardMachines, setIsLoadingCardMachines] = useState(true);

  useEffect(() => {
    const loadCompanyCardMachines = async () => {
      try {
        if (watch("company")?.value) {
          const { data } = await api.get("/Quote/TerminalPDQ", {
            params: { companyId: watch("company")?.value },
          });

          setCompanyCardMachines(data);
        }
        setIsLoadingCardMachines(false);
      } catch (error) {
        console.error(error);

        setIsLoadingCardMachines(false);
      }
    };

    loadCompanyCardMachines();
  }, [watch("company")]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body className="p-0">
          <div className="row g-0">
            {/* ── Form panel ─────────────────────────── */}
            <div
              className={`col-12 p-4 ${
                simulatorParcels.length
                  ? "col-xl-5 border-end"
                  : "col-xl-6 mx-auto"
              }`}
            >
              {/* Método de Pagamento */}
              <FS.FormSection>
                <FS.FormSectionTitle>
                  <i className="uil uil-exchange" />
                  <span>Método de Pagamento</span>
                </FS.FormSectionTitle>
                <Controller
                  name="method"
                  control={control}
                  render={({ field }) => (
                    <FS.ToggleGroup
                      role="group"
                      aria-label="Método de pagamento"
                    >
                      <FS.ToggleOption
                        type="button"
                        $active={field.value === PaymentMethod.Credit}
                        onClick={() => {
                          field.onChange(PaymentMethod.Credit);
                          changePaymentMethod(PaymentMethod.Credit);
                          setSimulatorParcels([]);
                        }}
                      >
                        Parcelamento
                      </FS.ToggleOption>
                      <FS.ToggleOption
                        type="button"
                        $active={field.value === PaymentMethod.Financing}
                        onClick={() => {
                          field.onChange(PaymentMethod.Financing);
                          changePaymentMethod(PaymentMethod.Financing);
                          setSimulatorParcels([]);
                        }}
                      >
                        Financiamento
                      </FS.ToggleOption>
                    </FS.ToggleGroup>
                  )}
                />
              </FS.FormSection>

              {/* Instituição */}
              <FS.FormSection>
                <FS.FormSectionTitle>
                  <i className="uil uil-building" />
                  <span>Instituição</span>
                </FS.FormSectionTitle>
                {userRole === RoleDescription.Admin ||
                userRole === RoleDescription.Patient ? (
                  <SelectSearch
                    placeholder="Buscar Instituição"
                    url="Company/search"
                    control={control}
                    name="company"
                    get={getCompanyOptions}
                    isClearable
                    error={errors.company?.label?.message}
                  />
                ) : (
                  <SelectDropdown
                    options={professionalCompanies.map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                    name="company"
                    placeholder="Selecione"
                    control={control}
                    error={errors.company?.label?.message}
                    isSearchable={false}
                    isDisabled={isLoadingProfessionalCompanies}
                  />
                )}
              </FS.FormSection>

              {/* Admin + Crédito: Tabela de Preços */}
              {userRole === RoleDescription.Admin &&
                watch("method") === PaymentMethod.Credit && (
                  <FS.FormSection>
                    <FS.FormSectionTitle>
                      <i className="uil uil-table" />
                      <span>Tabela de Preços</span>
                    </FS.FormSectionTitle>
                    <Controller
                      name="typePriceTable"
                      control={control}
                      render={({ field }) => (
                        <FS.ToggleGroup
                          role="group"
                          aria-label="Tabela preço por"
                        >
                          <FS.ToggleOption
                            type="button"
                            $active={
                              field.value === TypePriceTable.Companies
                            }
                            onClick={() => {
                              field.onChange(TypePriceTable.Companies);
                              changeTypePriceTable(TypePriceTable.Companies);
                              setSimulatorParcels([]);
                            }}
                          >
                            Instituição
                          </FS.ToggleOption>
                          <FS.ToggleOption
                            type="button"
                            $active={field.value === TypePriceTable.Tables}
                            onClick={() => {
                              field.onChange(TypePriceTable.Tables);
                              changeTypePriceTable(TypePriceTable.Tables);
                              setSimulatorParcels([]);
                            }}
                          >
                            Tabelas Padrões
                          </FS.ToggleOption>
                        </FS.ToggleGroup>
                      )}
                    />
                    {watch("typePriceTable") !== TypePriceTable.Companies && (
                      <div className="mt-3">
                        <PriceTableSelect
                          withCardMachines={true}
                          control={control}
                          customOnChange={() => validateValues(getValues())}
                        />
                      </div>
                    )}
                  </FS.FormSection>
                )}

              {/* Crédito (não-Tables): Ferramenta de Pagamento */}
              {watch("method") === PaymentMethod.Credit &&
                watch("typePriceTable") !== TypePriceTable.Tables && (
                  <FS.FormSection>
                    <FS.FormSectionTitle>
                      <i className="uil uil-processor" />
                      <span>Ferramenta de Pagamento</span>
                    </FS.FormSectionTitle>
                    <Controller
                      name="paymentTool"
                      control={control}
                      render={({ field }) => (
                        <FS.ToggleGroup
                          role="group"
                          aria-label="Ferramenta de pagamento"
                        >
                          <FS.ToggleOption
                            type="button"
                            $active={
                              field.value === PaymentTool["Link de pagamento"]
                            }
                            onClick={() => {
                              field.onChange(PaymentTool["Link de pagamento"]);
                              changePaymentTool();
                              setSimulatorParcels([]);
                            }}
                          >
                            Link de pagamento
                          </FS.ToggleOption>
                          <FS.ToggleOption
                            type="button"
                            $active={
                              field.value === PaymentTool["Máquina física"]
                            }
                            onClick={() => {
                              field.onChange(PaymentTool["Máquina física"]);
                              changePaymentTool();
                              setSimulatorParcels([]);
                            }}
                          >
                            Máquina física
                          </FS.ToggleOption>
                        </FS.ToggleGroup>
                      )}
                    />
                    {watch("paymentTool") === PaymentTool["Máquina física"] && (
                      <div className="mt-3">
                        <label className="label mb-1">Máquina</label>
                        <Controller
                          name="priceTable"
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <Select
                              options={companyCardMachines.map((m) => ({
                                value: m.id,
                                label: m.description,
                              }))}
                              onChange={(v) =>
                                onChange(
                                  v as { value: number; label: string }
                                )
                              }
                              placeholder="Buscar Máquina"
                              isClearable
                              value={value}
                              theme={customTheme}
                              styles={customStyles}
                              isDisabled={
                                isLoadingCardMachines || !getValues("company")
                              }
                            />
                          )}
                        />
                      </div>
                    )}
                  </FS.FormSection>
                )}

              {/* Crédito: Quem assume a taxa */}
              {watch("method") === PaymentMethod.Credit && (
                <FS.FormSection>
                  <FS.FormSectionTitle>
                    <i className="uil uil-percentage" />
                    <span>Quem assume a taxa?</span>
                  </FS.FormSectionTitle>
                  <Controller
                    name="paymentFeeOwner"
                    control={control}
                    render={({ field }) => (
                      <FS.ToggleGroup
                        role="group"
                        aria-label="Quem assume a taxa"
                      >
                        <FS.ToggleOption
                          type="button"
                          $active={field.value === PaymentFeeOwner.Paciente}
                          onClick={() => {
                            field.onChange(PaymentFeeOwner.Paciente);
                            const vals = getValues();
                            if (vals.value) {
                              onSubmit({
                                ...vals,
                                paymentFeeOwner: PaymentFeeOwner.Paciente,
                              });
                            }
                          }}
                        >
                          Cliente
                        </FS.ToggleOption>
                        <FS.ToggleOption
                          type="button"
                          $active={
                            field.value === PaymentFeeOwner.Estabelecimento
                          }
                          onClick={() => {
                            field.onChange(PaymentFeeOwner.Estabelecimento);
                            const vals = getValues();
                            if (vals.value) {
                              onSubmit({
                                ...vals,
                                paymentFeeOwner:
                                  PaymentFeeOwner.Estabelecimento,
                              });
                            }
                          }}
                        >
                          Estabelecimento
                        </FS.ToggleOption>
                      </FS.ToggleGroup>
                    )}
                  />
                </FS.FormSection>
              )}

              {/* Financiamento: Localização */}
              {watch("method") === PaymentMethod.Financing && (
                <FS.FormSection>
                  <FS.FormSectionTitle>
                    <i className="uil uil-map-marker" />
                    <span>Localização</span>
                  </FS.FormSectionTitle>
                  <div className="row g-3">
                    <div className="col-6">
                      <SelectInput
                        name="state"
                        label="Estado"
                        placeholder="Selecione"
                        control={control}
                        error={errors?.state?.message}
                        changeOptionSelected={handleSelectBrazilianState}
                        defaultValue=""
                        customOnChange={() => {
                          setSimulatorParcels([]);
                        }}
                      >
                        <MenuItem value="" disabled>
                          Selecione
                        </MenuItem>
                        {brazilianStates?.map((s) => (
                          <MenuItem key={s.id} value={s.sigla}>
                            {s.sigla}
                          </MenuItem>
                        ))}
                      </SelectInput>
                    </div>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        !watch("state") ? (
                          <Tooltip>Selecione primeiro o Estado</Tooltip>
                        ) : (
                          <></>
                        )
                      }
                    >
                      <div className="col-6">
                        <SelectInput
                          name="city"
                          label="Cidade"
                          placeholder="Selecione"
                          control={control}
                          error={errors?.city?.message}
                          isDisabled={!brazilianCities.length}
                          defaultValue=""
                          customOnChange={async () => {
                            handleFinancingValueOptions(getValues());
                            setSimulatorParcels([]);
                          }}
                        >
                          <MenuItem value="selecione" disabled>
                            Selecione
                          </MenuItem>
                          {brazilianCities?.map((c) => (
                            <MenuItem key={c.id} value={c.nome}>
                              {c.nome.toUpperCase()}
                            </MenuItem>
                          ))}
                        </SelectInput>
                      </div>
                    </OverlayTrigger>
                  </div>
                </FS.FormSection>
              )}

              {/* Valor */}
              <FS.FormSection>
                <FS.FormSectionTitle>
                  <i className="uil uil-money-bill" />
                  <span>Valor</span>
                </FS.FormSectionTitle>
                {watch("method") === PaymentMethod.Credit ? (
                  <>
                    <S.ValueRow className={errors.value ? "has-error" : ""}>
                      <S.ValuePrefix>R$</S.ValuePrefix>
                      <S.ValueInput
                        id="valor"
                        type="tel"
                        placeholder="0,00"
                        {...register("value")}
                        onChange={onChangeSearchInput}
                      />
                    </S.ValueRow>
                    {!!errors.value && (
                      <div className="text-danger mt-1">
                        <small>{errors.value?.message}</small>
                      </div>
                    )}
                  </>
                ) : (
                  <SelectDropdown
                    options={financingValueOptions}
                    name="value"
                    placeholder="Selecione o valor"
                    control={control}
                    error={errors.value?.message}
                    isSearchable={false}
                    customOnChange={() => {
                      const values = getValues();
                      if (values.state && values.city) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        values.value = values.value.value;
                        onSubmit(values);
                      } else {
                        setSimulatorParcels([]);
                      }
                    }}
                  />
                )}
              </FS.FormSection>

              {/* Disclaimer de Financiamento */}
              {watch("method") === PaymentMethod.Financing && (
                <S.Disclaimer className="animate__animated animate__fadeIn">
                  <p>*Valores de referência. Crédito sujeito à aprovação.</p>
                  <p className="mt-1">
                    *As taxas podem variar de 2% a.m. até 5% a.m. — dependem
                    da quantidade de parcelas, score do solicitante, seguro,
                    local de moradia/trabalho e IOF conforme legislação vigente.
                  </p>
                  <p className="mt-1">
                    *Não envie print da simulação ao paciente. Use como
                    referência e informe que a parcela é{" "}
                    <em>a partir do</em> valor simulado.
                  </p>
                </S.Disclaimer>
              )}
            </div>

            {/* ── Results panel ─────────────────────── */}
            {!!simulatorParcels.length && (
              <>
                <div className="col-12 d-xl-none">
                  <hr className="m-0" />
                </div>
                <div className="col-12 col-xl-7 p-4">
                  <S.ResultsHeader>
                    <h5>Simulação</h5>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Imprimir PDF</Tooltip>}
                    >
                      <Button
                        onClick={handlePrint}
                        className="btn btn-outline-primary border-0 rounded p-2"
                      >
                        <i className="uil uil-print" />
                      </Button>
                    </OverlayTrigger>
                  </S.ResultsHeader>

                  {isLoading ? (
                    <div>
                      {[...Array(3)].map((_, i) => (
                        <ListCardSkeleton key={i} />
                      ))}
                    </div>
                  ) : (
                    <S.ResultsScroll className="animate__animated animate__fadeIn">
                      {simulatorParcels.map((parcel, i) => (
                        <SimulatorParcelCard
                          key={i}
                          simulatorParcel={parcel}
                          company={getValues("company")}
                          showAddQuote={
                            getValues("priceTable") &&
                            getValues("paymentTool") !==
                              PaymentTool["Máquina física"]
                              ? false
                              : true
                          }
                          paymentToolAndMachine={{
                            paymentTool: getValues("paymentTool"),
                            cardMachine: getValues("priceTable"),
                          }}
                        />
                      ))}
                      <div style={{ display: "none" }}>
                        <div ref={componentRef} style={{ width: "182mm" }}>
                          <PDFSimulatorParcels
                            method={getValues("method")}
                            simulatorParcels={simulatorParcels}
                            company={getValues("company")}
                            value={getValues("value")}
                            currentPaymentFeeOwner={
                              getValues("paymentFeeOwner") ?? null
                            }
                            priceTable={getValues("priceTable") ?? null}
                          />
                        </div>
                      </div>
                    </S.ResultsScroll>
                  )}
                </div>
              </>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer
          className="justify-content-center bg-white position-sticky"
          style={{ bottom: 0 }}
        >
          <Button
            type="button"
            className="btn-light"
            onClick={onHide}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button type="submit" className="btn-primary" disabled={isLoading}>
            <i className="uil uil-check me-2" />
            Simular
            {!!isLoading && (
              <CircularProgress
                color="inherit"
                size={12}
                className="align-text-top ms-2"
              />
            )}
          </Button>
        </Modal.Footer>
      </form>
    </>
  );
};

export default Simulator;
